import type { IPageState } from "@rhc-shared-components/table-toolbar";
import { type QueryClient, type UseQueryResult, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchCompanyProfileDetails, getRedHatProductVersionsMetadata } from "../api/hydra";
import { getChecklistStatus, getProductScoreStatus } from "../api/pfs";
import { getProductAttachedComponents, getProductListingByIdAndMetadataGraphQL } from "../api/pyxis";
import { getProductListingVersionsAndCertifications } from "../api/superGraphQL";
import type {
	IChecklistStatusResponse,
	ICompanyDetails,
	IErrorType,
	IProductVersion,
	ProductScoreResponse,
} from "../constants/pfsContants";
import type { ISuperGraphProductVersion } from "../interfaces/graphql";
import type { SuperGraphProductListingResponse } from "../interfaces/superGraphQL";
import type { ContainerVendor, ProductListing } from "../types/generated-graphql";
import { FixProductListingData } from "../utils/fix-data";

export const redhatProductVersionsQueryKey = ["redhatProductVersions"] as const;

export const productScoreQueryKey = (productId: string) => ["productScore", productId] as const;

export const checklistStatusQueryKey = (productId: string) => ["checklistStatus", productId] as const;

export const productDetailQueryKey = (productId: string) => ["productDetail", productId] as const;

export const companyDetailsQueryKey = ["companyDetails"] as const;

export const vendorDetailsQueryKey = ["vendorDetails"] as const;

export const functionalCategoriesQueryKey = ["productFunctionalCategories"] as const;

export const useRedhatProductVersions = (): UseQueryResult<IProductVersion[], IErrorType> => {
	return useQuery<IProductVersion[], IErrorType>({
		queryKey: redhatProductVersionsQueryKey,
		queryFn: () => getRedHatProductVersionsMetadata(),
	});
};

export const useProductScore = (productId: string): UseQueryResult<ProductScoreResponse, Error> => {
	return useQuery<ProductScoreResponse, Error>({
		queryKey: productScoreQueryKey(productId),
		queryFn: ({ signal }) => getProductScoreStatus(productId, signal),
	});
};

export const useChecklistStatus = (productId: string): UseQueryResult<IChecklistStatusResponse, IErrorType> => {
	return useQuery<IChecklistStatusResponse, IErrorType>({
		queryKey: checklistStatusQueryKey(productId),
		queryFn: () => getChecklistStatus(productId),
	});
};

export const useProductDetail = (productId: string) => {
	const queryClient = useQueryClient();
	const isMetadataLoaded = !!queryClient.getQueryData(functionalCategoriesQueryKey);
	const isVendorMetadataLoaded = !!queryClient.getQueryData(vendorDetailsQueryKey);

	return useQuery<ProductListing, Error>({
		queryKey: productDetailQueryKey(productId),
		queryFn: async ({ signal }) => {
			const response = await getProductListingByIdAndMetadataGraphQL(
				productId,
				isMetadataLoaded,
				isVendorMetadataLoaded,
				signal,
			);
			const hasErrors = response?.errors && response.errors.length > 0;
			if (
				response?.data?.get_product_listing?.error ||
				response?.data?.functional_categories_metadata?.error ||
				response?.data?.vendor_metadata?.error ||
				hasErrors
			) {
				throw (
					response?.data?.get_product_listing?.error ||
					response?.data?.functional_categories_metadata?.error ||
					response?.data?.vendor_metadata?.error ||
					response?.errors?.[0]
				);
			} else {
				if (response?.data?.functional_categories_metadata?.data?.enum_values) {
					queryClient.setQueryDefaults(functionalCategoriesQueryKey, {
						staleTime: Infinity,
						gcTime: Infinity,
					});
					queryClient.setQueryData(
						functionalCategoriesQueryKey,
						response?.data?.functional_categories_metadata?.data?.enum_values || [],
					);
				}
				if (response?.data?.vendor_metadata?.data) {
					queryClient.setQueryDefaults(vendorDetailsQueryKey, {
						staleTime: Infinity,
						gcTime: Infinity,
					});
					queryClient.setQueryData(vendorDetailsQueryKey, response?.data?.vendor_metadata?.data);
				}
				// Recursively fetching the entire project list
				const projectPagination: IPageState = {
					page: 1,
					perPage: 100,
				};
				let hasMorePages = (response?.data?.get_product_listing?.data?.edges?.cert_projects?.total || 0) > 100;
				const projectListAPICalls = [];
				while (hasMorePages) {
					projectListAPICalls.push(getProductAttachedComponents(productId, projectPagination, signal));
					const totalItems = response?.data?.get_product_listing?.data?.edges?.cert_projects?.total || 0;
					const totalPages = Math.ceil(totalItems / projectPagination.perPage) || 1;
					hasMorePages = projectPagination.page < totalPages - 1;
					projectPagination.page = projectPagination.page + 1;
				}
				const pagedResponses = await Promise.all(projectListAPICalls);
				pagedResponses?.forEach((attachedComponentsResponse) => {
					attachedComponentsResponse?.data?.AttachedComponentsToProductsList?.data?.forEach((item) => {
						response?.data?.get_product_listing?.data?.edges?.cert_projects?.data?.push(item);
					});
				});
				return FixProductListingData(response?.data?.get_product_listing?.data as Record<string, any>);
			}
		},
	});
};

export const useCompanyDetails = (): UseQueryResult<ICompanyDetails, Error> => {
	return useQuery<ICompanyDetails, Error>({
		queryKey: companyDetailsQueryKey,
		queryFn: ({ signal }) => fetchCompanyProfileDetails(signal),
	});
};

export const useVendorDetails = () => {
	const { data } = useQuery<Pick<ContainerVendor, "allow_hosted_container_registry">, Error>({
		queryKey: vendorDetailsQueryKey,
		queryFn: async () => {
			// This query is populated by useProductDetail, so we just read from cache
			return {} as Pick<ContainerVendor, "allow_hosted_container_registry">;
		},
		enabled: false, // Never auto-fetch, only read from cache set by useProductDetail
		staleTime: Infinity,
	});
	return data;
};

export const useFunctionalCategories = () => {
	const { data } = useQuery<string[], Error>({
		queryKey: functionalCategoriesQueryKey,
		queryFn: async () => {
			// This query is populated by useProductDetail, so we just read from cache
			return [];
		},
		enabled: false, // Never auto-fetch, only read from cache set by useProductDetail
		staleTime: Infinity,
	});
	return data || [];
};

/**
 * Invalidates product-related queries that should refetch when product state changes
 * Use this after actions like publish/unpublish, adding/removing components, etc.
 */
export const invalidateProductQueries = async (queryClient: QueryClient, productId: string) => {
	await Promise.all([
		queryClient.invalidateQueries({ queryKey: productScoreQueryKey(productId) }),
		queryClient.invalidateQueries({ queryKey: checklistStatusQueryKey(productId) }),
		queryClient.invalidateQueries({ queryKey: superGraphProductListingQueryKey(productId) }),
	]);
};

export const superGraphProductListingQueryKey = (productId: string) => ["superGraphProductListing", productId] as const;

export const useSuperGraphProductListing = (productId: string) => {
	return useQuery<SuperGraphProductListingResponse, Error, ISuperGraphProductVersion[]>({
		queryKey: superGraphProductListingQueryKey(productId),
		queryFn: ({ signal }) => getProductListingVersionsAndCertifications(productId, signal),
		select: (response): ISuperGraphProductVersion[] => {
			const certifications = response?.data?.product_listing?.functional_info?.certifications || [];
			return certifications.map((cert) => ({
				product_version: cert.partnerProductVersion,
				certificationLevel: cert.certificationLevel,
				redhat_affinity: [{ product_name: cert.product, versions: [cert.majorVersion] }],
			}));
		},
		enabled: !!productId,
		retry: false,
		retryOnMount: false,
		refetchOnMount: false,
	});
};
